/**
 * ISO 3166-1 alpha-2 countries, for the places that ask an operator where
 * they are.
 *
 * A free-text two-letter box accepted "XX", "gb ", or a typo that looked
 * plausible and was stored forever — the code is a foreign-key-shaped fact
 * (it lines up with `ref_jurisdictions.country`), so guessing at it is the
 * one thing the field should not allow. Derived from the runtime's own
 * region names where available so the labels are localised and never drift,
 * with the code list itself held here because `Intl` offers no way to
 * enumerate regions.
 *
 * The full ISO list (migration 0190's `ref_countries`, `packages/db/migrations/
 * 0190_brand_territories.sql`) rather than the 66-code subset this used to
 * hold: a brand can be in any country (owner requirement, 2026-09-21), so a
 * picker that named only sixty-six would refuse the other operators'. Held
 * here rather than imported from `@affdice/db`, because this package has no
 * dependency on it — the same 250 codes (249 ISO-assigned + XK), kept from
 * drifting apart because one migration author wrote both lists from the same
 * generated source in the same change (`packages/db/test/brand-territory.test.ts`
 * pins `ref_countries` at 250 rows, which is the number to keep this array's
 * length equal to on the next country either list adds).
 */

const CODES = [
  'AD', 'AE', 'AF', 'AG', 'AI', 'AL', 'AM', 'AO', 'AQ', 'AR', 'AS', 'AT', 'AU',
  'AW', 'AX', 'AZ', 'BA', 'BB', 'BD', 'BE', 'BF', 'BG', 'BH', 'BI', 'BJ', 'BL',
  'BM', 'BN', 'BO', 'BQ', 'BR', 'BS', 'BT', 'BV', 'BW', 'BY', 'BZ', 'CA', 'CC',
  'CD', 'CF', 'CG', 'CH', 'CI', 'CK', 'CL', 'CM', 'CN', 'CO', 'CR', 'CU', 'CV',
  'CW', 'CX', 'CY', 'CZ', 'DE', 'DJ', 'DK', 'DM', 'DO', 'DZ', 'EC', 'EE', 'EG',
  'EH', 'ER', 'ES', 'ET', 'FI', 'FJ', 'FK', 'FM', 'FO', 'FR', 'GA', 'GB', 'GD',
  'GE', 'GF', 'GG', 'GH', 'GI', 'GL', 'GM', 'GN', 'GP', 'GQ', 'GR', 'GS', 'GT',
  'GU', 'GW', 'GY', 'HK', 'HM', 'HN', 'HR', 'HT', 'HU', 'ID', 'IE', 'IL', 'IM',
  'IN', 'IO', 'IQ', 'IR', 'IS', 'IT', 'JE', 'JM', 'JO', 'JP', 'KE', 'KG', 'KH',
  'KI', 'KM', 'KN', 'KP', 'KR', 'KW', 'KY', 'KZ', 'LA', 'LB', 'LC', 'LI', 'LK',
  'LR', 'LS', 'LT', 'LU', 'LV', 'LY', 'MA', 'MC', 'MD', 'ME', 'MF', 'MG', 'MH',
  'MK', 'ML', 'MM', 'MN', 'MO', 'MP', 'MQ', 'MR', 'MS', 'MT', 'MU', 'MV', 'MW',
  'MX', 'MY', 'MZ', 'NA', 'NC', 'NE', 'NF', 'NG', 'NI', 'NL', 'NO', 'NP', 'NR',
  'NU', 'NZ', 'OM', 'PA', 'PE', 'PF', 'PG', 'PH', 'PK', 'PL', 'PM', 'PN', 'PR',
  'PS', 'PT', 'PW', 'PY', 'QA', 'RE', 'RO', 'RS', 'RU', 'RW', 'SA', 'SB', 'SC',
  'SD', 'SE', 'SG', 'SH', 'SI', 'SJ', 'SK', 'SL', 'SM', 'SN', 'SO', 'SR', 'SS',
  'ST', 'SV', 'SX', 'SY', 'SZ', 'TC', 'TD', 'TF', 'TG', 'TH', 'TJ', 'TK', 'TL',
  'TM', 'TN', 'TO', 'TR', 'TT', 'TV', 'TW', 'TZ', 'UA', 'UG', 'UM', 'US', 'UY',
  'UZ', 'VA', 'VC', 'VE', 'VG', 'VI', 'VN', 'VU', 'WF', 'WS', 'XK', 'YE', 'YT',
  'ZA', 'ZM', 'ZW',
] as const;

export type CountryCode = (typeof CODES)[number];

let cached: readonly { value: string; label: string }[] | undefined;

export function countryOptions(): readonly { value: string; label: string }[] {
  if (!cached) {
    let name: (code: string) => string = (code) => code;
    try {
      const display = new Intl.DisplayNames(['en'], { type: 'region' });
      name = (code) => display.of(code) ?? code;
    } catch {
      // No Intl.DisplayNames — the codes alone are still selectable, which is
      // the part that matters. A missing label is not a reason to fall back to
      // a free-text box that accepts anything.
    }
    cached = [...CODES]
      .map((code) => ({ value: code, label: `${name(code)} (${code})` }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }
  return cached;
}
